import { useState, useEffect, useRef } from 'react';
import StaffNav from './managernav';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import MeetingCalendar from './meetingscalenderlite';
import { useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { connectGoogleCalendar } from '../utils/googleSync';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'

// ─── ICS Parser ──────────────────────────────────────────────────────────────

function unfoldICSLines(raw) {
    return raw
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n')
        .reduce((acc, line) => {
            if ((line.startsWith(' ') || line.startsWith('\t')) && acc.length > 0) {
                acc[acc.length - 1] += line.slice(1);
            } else {
                acc.push(line);
            }
            return acc;
        }, []);
}

function parseICSDateTime(dtstring) {
    if (!dtstring) return null;
    // Strip VALUE=DATE: prefix if present, e.g. "VALUE=DATE:20250101"
    const clean = dtstring.replace(/^VALUE=DATE:/i, '').replace('Z', '');
    if (clean.includes('T')) {
        const [datePart, timePart] = clean.split('T');
        return {
            date: `${datePart.slice(0, 4)}-${datePart.slice(4, 6)}-${datePart.slice(6, 8)}`,
            time: `${timePart.slice(0, 2)}:${timePart.slice(2, 4)}`,
            raw: new Date(`${datePart.slice(0, 4)}-${datePart.slice(4, 6)}-${datePart.slice(6, 8)}T${timePart.slice(0, 2)}:${timePart.slice(2, 4)}:00`),
        };
    }
    return {
        date: `${clean.slice(0, 4)}-${clean.slice(4, 6)}-${clean.slice(6, 8)}`,
        time: null,
        raw: new Date(`${clean.slice(0, 4)}-${clean.slice(4, 6)}-${clean.slice(6, 8)}`),
    };
}

function snapToMeetingLength(diffMins) {
    const options = [30, 60, 90, 120, 150, 180];
    return String(options.reduce((a, b) => (Math.abs(b - diffMins) < Math.abs(a - diffMins) ? b : a)));
}

function mapICSLocation(raw = '') {
    const lower = raw.toLowerCase();
    if (lower.includes('meet.google.com') || lower.includes('google meet')) return 'Google Meet';
    if (lower.includes('meeting room') || lower.includes('boardroom')) return 'Meeting Room';
    return 'Other';
}

function parseICSFile(content) {
    const lines = unfoldICSLines(content);
    const result = { summary: '', date: '', time: '', location: '', notes: '', meetinglength: '', attendeeNames: [] };

    let inEvent = false;
    let dtstart = null;
    let dtend = null;

    for (const line of lines) {
        const colonIdx = line.indexOf(':');
        if (colonIdx === -1) continue;
        // Key may include parameters, e.g. DTSTART;TZID=Europe/London
        const rawKey = line.slice(0, colonIdx);
        const key = rawKey.split(';')[0].toUpperCase();
        const value = line.slice(colonIdx + 1).trim();

        if (key === 'BEGIN' && value === 'VEVENT') { inEvent = true; continue; }
        if (key === 'END' && value === 'VEVENT') { inEvent = false; continue; }
        if (!inEvent) continue;

        switch (key) {
            case 'SUMMARY':
                result.summary = value.replace(/\\,/g, ',').replace(/\\n/g, ' ').replace(/\\\\/g, '\\');
                break;
            case 'DESCRIPTION':
                result.notes = value.replace(/\\n/g, '\n').replace(/\\,/g, ',').replace(/\\\\/g, '\\');
                break;
            case 'LOCATION':
                result.rawLocation = value.replace(/\\,/g, ',');
                break;
            case 'DTSTART':
                dtstart = parseICSDateTime(value);
                break;
            case 'DTEND':
                dtend = parseICSDateTime(value);
                break;
            case 'ATTENDEE': {
                // CN="John Smith" or CN=John Smith
                const cnMatch = rawKey.match(/CN="?([^";]+)"?/i) || value.match(/CN="?([^";:]+)"?/i);
                const emailMatch = value.match(/mailto:(.+)/i);
                const name = cnMatch ? cnMatch[1].trim() : (emailMatch ? emailMatch[1].trim() : null);
                if (name) result.attendeeNames.push(name);
                break;
            }
            default: break;
        }
    }

    if (dtstart) {
        result.date = dtstart.date;
        result.time = dtstart.time || '';
    }

    if (dtstart?.raw && dtend?.raw) {
        const diffMins = Math.round((dtend.raw - dtstart.raw) / 60000);
        if (diffMins > 0) result.meetinglength = snapToMeetingLength(diffMins);
    }

    if (result.rawLocation) {
        result.location = mapICSLocation(result.rawLocation);
    }

    return result;
}

export default function Meeting() {

    const Navigate = useNavigate();
    const location = useLocation();

    const user = useContext(AuthContext);
    const avenorLeadId = Number(location?.state?.avenorLeadId);
    const avenorDefaults = location?.state?.defaults || null;



    const token = localStorage.getItem('token');

    const [staff, setStaff] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        date: '',
        time: '',
        attendees: [],
        trainingDocument: '',
        location: '',
        notes: '',
        type: '',
        appraisal: '',
        address : '',
        postcode: '',
        clientName: '',
        meetinglength: '',
        project : ''


    });
    const [trainingDocuments, setTrainingDocuments] = useState([]);
    const [appraisal, setAppraisal] = useState([]);
    const [id, setId] = useState(null);
    const [date, setDate] = useState(null);
    const [drivingTime, setDrivingTime] = useState(null);
    const [distance, setDistance] = useState(null);
    const [message, setMessage] = useState(null);
    const [googleMeetWarning, setGoogleMeetWarning] = useState('');
    const [projects, setProjects] = useState([]);
    const [icsNotice, setIcsNotice] = useState('');
    const icsInputRef = useRef(null);

    const controlClass = 'block w-full rounded-xl border border-indigo-200/70 bg-white/80 py-2 px-3 text-sm text-indigo-900 shadow-sm placeholder:text-indigo-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-300/70';
    const labelClass = 'block text-sm font-medium text-indigo-900 sm:pt-1.5';

    const handleICSImport = (e) => {
        const file = e.target.files?.[0];
        if (!icsInputRef.current) return;
        icsInputRef.current.value = '';
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const parsed = parseICSFile(ev.target.result);
                const fieldsSet = [];

                setFormData((prev) => {
                    const next = { ...prev };

                    if (parsed.summary) { next.name = parsed.summary; fieldsSet.push('name'); }
                    if (parsed.date) { next.date = parsed.date; setDate(parsed.date); fieldsSet.push('date'); }
                    if (parsed.time) { next.time = parsed.time; fieldsSet.push('time'); }
                    if (parsed.notes) { next.notes = parsed.notes; fieldsSet.push('notes'); }
                    if (parsed.meetinglength) { next.meetinglength = parsed.meetinglength; fieldsSet.push('meeting length'); }

                    if (parsed.location) {
                        next.location = parsed.location;
                        fieldsSet.push('location');
                    }

                    // Match attendee names from ICS against loaded staff list
                    if (parsed.attendeeNames.length > 0) {
                        const matched = parsed.attendeeNames.filter((icsName) =>
                            staff.some((s) => s.Name.toLowerCase() === icsName.toLowerCase())
                        );
                        const matchedStaffNames = matched.map((icsName) =>
                            staff.find((s) => s.Name.toLowerCase() === icsName.toLowerCase()).Name
                        );
                        if (matchedStaffNames.length > 0) {
                            next.attendees = [...new Set([...prev.attendees, ...matchedStaffNames])];
                            fieldsSet.push(`${matchedStaffNames.length} attendee(s)`);
                        }
                    }

                    return next;
                });

                if (fieldsSet.length > 0) {
                    setIcsNotice(`Imported from ICS: ${fieldsSet.join(', ')}.`);
                } else {
                    setIcsNotice('ICS file parsed but no matching fields were found.');
                }
            } catch {
                setIcsNotice('Could not read ICS file. Please check the file and try again.');
            }
        };
        reader.readAsText(file);
    };

    console.log('formData', formData);
    console.log('appraisal', id);

    useEffect(() => {
        if (!avenorDefaults) {
            return;
        }

        setFormData((previous) => ({
            ...previous,
            name: avenorDefaults.name || previous.name,
            location: avenorDefaults.location || previous.location,
            clientName: avenorDefaults.clientName || previous.clientName,
            address: avenorDefaults.address || previous.address,
            postcode: avenorDefaults.postcode || previous.postcode,
            notes: avenorDefaults.notes || previous.notes,
        }));
    }, [avenorDefaults]);

    useEffect(() => {
        const fetchDisciplinary = async () => {
            if (formData.type === 'Disciplinary' && formData.attendees.length > 0) {
                try {
                    // Extract IDs of selected staff
    
                    // Fetch disciplinary records for selected staff
                    const response = await fetch('https://api.insightos.co.uk/api/alldisciplinary', {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',

                    });

                      
    
                    if (!response.ok) {
                        throw new Error('Failed to fetch disciplinary records');
                    }
    
                    const data = await response.json();
                    setAppraisal(data.data); // Set disciplinary records to state
                } catch (error) {
                    console.error('Error fetching disciplinary records:', error);
                }
            }
        }

        fetchDisciplinary();

    }, [formData.type]);




    useEffect(() => {
        const fetchAppraisals = async () => {
            if (formData.type === 'Appraisal' && formData.attendees.length > 0) {
                try {
                    // Extract IDs of selected staff

                    const selectedStaffIds = formData.attendees.map((attendee) => staff.find((s) => s.Name === attendee).id).join(',');

                    
                    console.log('selectedStaffIds', selectedStaffIds);                      
                   
    
                    // Fetch appraisals for selected staff
                    const response = await fetch(`https://api.insightos.co.uk/api/viewappraisal/${selectedStaffIds}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials : 'include',


                    });


                    if (!response.ok) {
                        throw new Error('Failed to fetch appraisals');
                    }
    
                    const data = await response.json();
                    console.log('data', data.data);
                    setAppraisal(data.data); // Set appraisals to state
                } catch (error) {
                    console.error('Error fetching appraisals:', error);
                }
            }
        };
    
        fetchAppraisals();
    }, [formData.type]);
    
console.log('appraisal', formData.attendees.id);


    // Fetch staff data
    useEffect(() => {
        const getStaff = async () => {


            const response = await fetch('https://api.insightos.co.uk/api/staff', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials : 'include',

            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setStaff(data.data);
        };
        getStaff();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setDate(formData.date);
        if (name === 'location' && value !== 'Google Meet') {
            setGoogleMeetWarning('');
        }
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const toggleAttendee = (name) => {
        setFormData((prevFormData) => {
            const attendees = prevFormData.attendees.includes(name)
                ? prevFormData.attendees.filter((attendee) => attendee !== name) // Remove attendee
                : [...prevFormData.attendees, name]; // Add attendee
            return { ...prevFormData, attendees };
        });
    };
    
    

    const handleSubmit = async (e) => {
        e.preventDefault();


        




        const meetingData = {
            Name: formData.name,
            Date: formData.date,
            Time: formData.time,
            Attendees: formData.attendees, // array of selected attendees
            TrainingDocName: formData.trainingDocument,
            Location: formData.location,
            Notes: formData.notes,
            Type: formData.type,
            DocType: formData.appraisal,
            BookedBy: user.email,
            Address: formData.address,
            Postcode: formData.postcode,
            ClientName: formData.clientName,
            MeetingLength: formData.meetinglength,
            TotalReturnDistance: formData.location === "Client Visits" ? drivingTime[0].returnDistance : null,
            TotalReturnTime: formData.location === "Client Visits" ? drivingTime[0].returnDrivingTime : null,
            TotalMeetingTime: formData.location === "Client Visits" ? drivingTime[0].totalMeetingTime : null,
            LeavingTime: formData.location === "Client Visits" ? drivingTime[0].estimatedLeavingTime : null,
            ReturnTime: formData.location === "Client Visits" ? drivingTime[0].estimatedReturnTime : null,
            ProjectId : formData.project,
            Status : formData.status
          };
          

        const response = await fetch('https://api.insightos.co.uk/api/addmeeting', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials : 'include',

            body: JSON.stringify(meetingData),
        });

        const responseData = await response.json();

        if (response.ok) {
            if (formData.location === 'Google Meet' && responseData?.googleMeet?.status !== 'created') {
                setGoogleMeetWarning(responseData?.googleMeet?.reason || 'Google Meet URL was not created. Reconnect Google Calendar and try again.');
                return;
            }

            setGoogleMeetWarning('');
            if (Number.isInteger(avenorLeadId) && avenorLeadId > 0 && Number(responseData?.meetingId) > 0) {
                const linkResponse = await fetch(`https://api.insightos.co.uk/api/avenor/pipeline/leads/${avenorLeadId}/meetings/link`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({ meetingId: Number(responseData.meetingId) }),
                });

                const linkData = await linkResponse.json().catch(() => null);
                if (!linkResponse.ok || !linkData?.success) {
                    console.error('Failed to link meeting to Avenor lead', linkData || linkResponse.statusText);
                }
            }
            if (responseData?.overwrittenTasks?.length > 0) {
                Navigate('/overwritten-tasks', { state: { overwrittenTasks: responseData.overwrittenTasks } });
            } else {
                Navigate('/dashboard');
            }
        } else {
            console.error('Failed to save meeting');
        }
    };

    useEffect(() => {
        const fetchTrainingDocuments = async () => {
          try {
            const response = await fetch('https://api.insightos.co.uk/api/trainingdocuments', {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',

              },
              credentials : 'include',

            });
      
            if (response.ok) {
              const data = await response.json();
      
              // Transform data: group by TrainingDocName
              const groupedData = data.data.reduce((acc, item) => {
                const { TrainingDocName, Title, Page, Code } = item;
      
                // Initialize group if it doesn't exist
                if (!acc[TrainingDocName]) {
                  acc[TrainingDocName] = { TrainingDocName, Pages: [] };
                }
      
                // Add current item to the Pages array
                acc[TrainingDocName].Pages.push({ Title, Page, Code });
      
                return acc;
              }, {});
      
              // Convert grouped object back to an array and sort Pages by Page number
              const groupedArray = Object.values(groupedData).map((group) => ({
                ...group,
                Pages: group.Pages.sort((a, b) => a.Page - b.Page), // Sort Pages by Page number
              }));
      
              console.log(groupedArray); // Verify grouped and sorted data
              setTrainingDocuments(groupedArray);
            } else {
              console.error('Failed to fetch training documents');
            }
          } catch (error) {
            console.error('Error:', error);
          }
        };
      
        fetchTrainingDocuments();
      }, []);

      const handleDrivingTime = async () => {
        try {
            const response = await fetch(`https://api.insightos.co.uk/api/drivingtime/${formData.postcode}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',

            });
    
            if (!response.ok) {
                throw new Error(`Failed to fetch driving time: ${response.statusText}`);
            }
    
            const data = await response.json();
            console.log('Driving Time Data:', data.drivingTime, data.distance);
    
            // Convert driving time (mins) and distance (miles) into numbers
            const driveTime = parseInt(data.drivingTime, 10);
            const distance = parseFloat(data.distance);
    
            // Convert driving time to hours and minutes
            const driveHours = Math.floor(driveTime / 60);
            const driveMinutes = driveTime % 60;
            const formattedDriveTime = `${driveHours}h ${driveMinutes}m`;
    
            // Calculate return journey time and distance
            const returnDrivingTime = driveTime * 2;
            const returnDistance = distance * 2;
    
            // Calculate total journey including meeting length & extra traffic time (30 min buffer)
            const totalMeetingTime = returnDrivingTime + parseInt(formData.meetinglength, 10) + 30;
    
            // Convert total time to hours and minutes
            const totalHours = Math.floor(totalMeetingTime / 60);
            const totalMinutes = totalMeetingTime % 60;
            const formattedTotalMeetingTime = `${totalHours}h ${totalMinutes}m`;
    
            // Parse formData.time (meeting start time in HH:mm)
            const [meetingHours, meetingMinutes] = formData.time.split(":").map(Number);
            const meetingStart = new Date();
            meetingStart.setHours(meetingHours, meetingMinutes, 0, 0);
    
            // Calculate estimated leaving time (subtract one-way drive time)
            const leavingTime = new Date(meetingStart);
            leavingTime.setMinutes(leavingTime.getMinutes() - (driveTime + 15)); // Leaving 15 mins early
    
            // Calculate estimated return time (add total time)
            const returnTime = new Date(meetingStart);
            returnTime.setMinutes(returnTime.getMinutes() + totalMeetingTime);
    
            // Format time to 12-hour clock with AM/PM using date-fns
            const formatTime = (date) => format(date, 'HH:mm');
    
            // Turn values into an array of objects
            const drivingDetails = [
                {
                    drivingTime: formattedDriveTime,
                    distance: `${distance.toFixed(1)} miles`,
                    returnDrivingTime: `${Math.floor(returnDrivingTime / 60)}h ${returnDrivingTime % 60}m`,
                    returnDistance: `${returnDistance.toFixed(1)} miles`,
                    totalMeetingTime: formattedTotalMeetingTime,
                    estimatedLeavingTime: formatTime(leavingTime),
                    estimatedReturnTime: formatTime(returnTime),
                },
            ];
    
            setDrivingTime(drivingDetails);
        } catch (error) {
            console.error('Error fetching driving time:', error);
        }
    };
    

           


    useEffect(() => {
        const fetchMeeting = async () => {
            if (!formData.date || !formData.time || !formData.meetinglength) {
                return; // Do nothing if any field is missing
            }
    
            try {
                const response = await fetch(`https://api.insightos.co.uk/api/meetings/${formData.date}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',

                });
    
                if (!response.ok) {
                    throw new Error('Failed to fetch meetings');
                }
    
                const data = await response.json();
                console.log('Fetched meetings:', data.data);
    
                // Ensure the time is in "HH:mm:ss" format
                const selectedStartTime = `${formData.time}:00`.trim();
                const meetingDuration = parseInt(formData.meetinglength, 10); // Get the duration in minutes
                console.log('Selected Start Time:', selectedStartTime, 'Duration:', meetingDuration);
    
                // Convert selected time to minutes for easier comparison
                const [hh, mm, ss] = selectedStartTime.split(':').map(Number);
                const selectedStartMinutes = hh * 60 + mm; // Convert to total minutes
                const selectedFinishMinutes = selectedStartMinutes + meetingDuration; // Calculate new FinishTime
    
                console.log('Selected Finish Minutes:', selectedFinishMinutes);
    
                const meetingConflict = data.data.find((m) => {
                    const dbDate = m.Date.split("T")[0]; // Extract only "YYYY-MM-DD" part
                    const dbStartTime = m.Time.trim(); // Meeting start time (HH:mm:ss)
                    const dbFinishTime = m.FinishTime.trim(); // Meeting finish time (HH:mm:ss)
    
                    // Convert DB times to minutes for comparison
                    const [dbStartH, dbStartM, dbStartS] = dbStartTime.split(':').map(Number);
                    const dbStartMinutes = dbStartH * 60 + dbStartM;
    
                    const [dbFinishH, dbFinishM, dbFinishS] = dbFinishTime.split(':').map(Number);
                    const dbFinishMinutes = dbFinishH * 60 + dbFinishM;
    
                    console.log(`Checking DB Meeting: ${dbStartTime} - ${dbFinishTime} against ${selectedStartTime} - ${selectedFinishMinutes}`);
    
                    // Check if the new meeting overlaps with any existing meeting
                    return (
                        dbDate === formData.date &&
                        (
                            (selectedStartMinutes >= dbStartMinutes && selectedStartMinutes < dbFinishMinutes) || // Starts inside existing meeting
                            (selectedFinishMinutes > dbStartMinutes && selectedFinishMinutes <= dbFinishMinutes) || // Ends inside existing meeting
                            (selectedStartMinutes <= dbStartMinutes && selectedFinishMinutes >= dbFinishMinutes) // Completely overlaps existing
                        )
                    );
                });
    
                console.log('Meeting conflict found:', meetingConflict);
    
                if (meetingConflict) {
                    setMessage("This meeting overlaps with another scheduled meeting.");
    
                    // Reset only the time field
                    setFormData((prevData) => ({
                        ...prevData,
                        time: '',
                    }));
                } else {
                    setMessage(""); // Clear message if no conflict
                }
            } catch (error) {
                console.error("Error fetching meetings:", error);
            }
        };
    
        fetchMeeting();
    }, [formData.date, formData.time, formData.meetinglength]); // ✅ Runs when date, time, or length changes
    


        
useEffect(() => {

    const fetchProjects = async () => {
        if (formData.type === 'Project Meeting' ) {
            try {
                const response = await fetch('https://api.insightos.co.uk/api/getproject', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json', 
                    },
                    credentials: 'include',


                });

                if (!response.ok) {
                    throw new Error('Failed to fetch projects');
                }

                const data = await response.json();

                setProjects(data.data);

            } catch (error) {

                console.error('Error fetching projects:', error);

            }

        }

    };

    fetchProjects();

}, [formData.type]);









      

    return (
        <>
            <StaffNav />
            <div className="mx-auto w-full max-w-7xl px-2 sm:px-4 pb-24 md:pb-12">
            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <form onSubmit={handleSubmit} className="w-full lg:col-span-7 rounded-2xl bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border border-indigo-200/60 shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-4 sm:p-6">
                <div className="space-y-8">
                    <div className="rounded-xl border border-indigo-200/70 bg-white/70 p-4 sm:p-5">
                        <h2 className="text-lg font-semibold text-indigo-900">Create a Meeting</h2>
                        <p className="mt-1 text-sm text-indigo-700">Book meetings with the same flow and styling as your calendar page.</p>

                        {/* ICS Import */}
                        <div className="mt-4 flex items-center gap-x-3">
                            <input
                                ref={icsInputRef}
                                type="file"
                                accept=".ics,text/calendar"
                                className="hidden"
                                onChange={handleICSImport}
                            />
                            <button
                                type="button"
                                onClick={() => icsInputRef.current?.click()}
                                className="inline-flex items-center gap-x-1.5 rounded-xl border border-indigo-200/70 bg-white/85 px-3 py-2 text-xs font-semibold text-indigo-900 shadow-sm hover:bg-indigo-50"
                            >
                                <svg className="h-4 w-4 text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                </svg>
                                Import from .ics file
                            </button>
                            {icsNotice && (
                                <p className={`text-xs ${icsNotice.startsWith('Could') ? 'text-rose-600' : 'text-indigo-600'}`}>
                                    {icsNotice}
                                </p>
                            )}
                        </div>

                        {googleMeetWarning && (
                            <div className="mt-4 rounded-xl border border-rose-200/70 bg-rose-50/90 p-3 text-sm text-rose-700">
                                <p className="font-semibold">Google Meet link was not created.</p>
                                <p className="mt-1">{googleMeetWarning}</p>
                                <button
                                    type="button"
                                    onClick={connectGoogleCalendar}
                                    className="mt-3 inline-flex items-center gap-x-1.5 rounded-xl border border-indigo-200/70 bg-white/85 px-3 py-2 text-xs font-semibold text-indigo-900 shadow-sm hover:bg-white"
                                >
                                    <img className="h-4 w-4" src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" />
                                    Reconnect Google Calendar
                                </button>
                            </div>
                        )}

                        <div className="mt-6 space-y-8 border-b border-indigo-200/60 pb-8 sm:space-y-0 sm:divide-y sm:divide-indigo-100/80 sm:border-t sm:border-indigo-200/60 sm:pb-0">
                            {/* Meeting Name */}
                            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                <label htmlFor="name" className={labelClass}>
                                    Meeting Name
                                </label>
                                <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        placeholder="Marketing"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={controlClass}
                                    />
                                </div>
                            </div>


                            {/* Date */}
                            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                <label htmlFor="date" className={labelClass}>
                                    Date
                                </label>
                                <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
                                    <input
                                        id="date"
                                        name="date"
                                        type="date"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        className={controlClass}
                                    />
                                </div>
                            </div>

                            {/* Time */}
                            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                <label htmlFor="time" className={labelClass}>
                                    Time
                                </label>
                                <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
                                    <input
                                        id="time"
                                        name="time"
                                        type="time"
                                        value={formData.time}
                                        onChange={handleInputChange}
                                        className={controlClass}
                                    />
                                </div>
                            </div>
                            {message && (
                                <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                    <p className="text-red-600">{message}</p>
                                </div>
                            )}
                            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                            <label htmlFor="meetinglength" className={labelClass}>
                                    Meeting Length
                                </label>
                                <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
                                    <select
                                        id="meetinglength"
                                        name="meetinglength"
                                        value={formData.meetinglength}
                                        onChange={handleInputChange}
                                        className={controlClass}
                                    >
                                        <option value="" disabled>Select Meeting Length</option>
                                        <option value="30">30 Minutes</option>
                                        <option value="60">1 Hour</option>
                                        <option value="90">1 Hour 30 Minutes</option>
                                        <option value="120">2 Hours</option>
                                        <option value="150">2 Hours 30 Minutes</option>
                                        <option value="180">3 Hours</option>

                                    </select>
                                </div>
                            </div>


                            {/* Location */}
                            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                <label className={labelClass}>
                                    Location of Meeting
                                </label>
                                <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
                                    <Listbox value={formData.location} onChange={(val) => handleInputChange({ target: { name: 'location', value: val } })}>
                                        <div className="relative mt-2">
                                            <ListboxButton className="relative w-full cursor-default rounded-xl border border-indigo-200/70 bg-white/80 py-2 pl-3 pr-10 text-left text-sm text-indigo-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-300/70">
                                                <span className="flex items-center">
                                                    {formData.location === 'Google Meet' && (
                                                        <img src="https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v6/web-512dp/logo_meet_2020q4_color_1x_web_512dp.png" alt="Google Meet" className="h-5 w-5 flex-shrink-0 mr-2" />
                                                    )}
                                                    <span className="block truncate">{formData.location || 'Select Location'}</span>
                                                </span>
                                                <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                                </span>
                                            </ListboxButton>

                                            <ListboxOptions className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-indigo-200/70 bg-white py-1 text-sm shadow-lg focus:outline-none">
                                                {['Meeting Room', 'Google Meet', 'Client Visits', 'Other'].map((option) => (
                                                    <ListboxOption
                                                        key={option}
                                                        className={({ active }) =>
                                                            `relative cursor-default select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                                            }`
                                                        }
                                                        value={option}
                                                    >
                                                        {({ selected, active }) => (
                                                            <>
                                                                <div className="flex items-center">
                                                                    {option === 'Google Meet' && (
                                                                        <img src="https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v6/web-512dp/logo_meet_2020q4_color_1x_web_512dp.png" alt="Google Meet" className="h-5 w-5 flex-shrink-0 mr-2" />
                                                                    )}
                                                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                                                        {option}
                                                                    </span>
                                                                </div>

                                                                {selected ? (
                                                                    <span
                                                                        className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'
                                                                            }`}
                                                                    >
                                                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                                    </span>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </ListboxOption>
                                                ))}
                                            </ListboxOptions>
                                        </div>
                                    </Listbox>
                                </div>
                            </div>
                            {formData.location === 'Client Visits' && (
  <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
    {/* Client Name */}
    <label htmlFor="clientName" className="block text-sm font-medium text-gray-900">
      Client Name
    </label>
    <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
      <input
        id="clientName"
        name="clientName"
        type="text"
        value={formData.clientName}
        onChange={handleInputChange}
        className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
      />
    </div>

    {/* Address */}
    <label htmlFor="address" className="block text-sm font-medium text-gray-900">
      Address
    </label>
    <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
      <input
        id="address"
        name="address"
        type="text"
        value={formData.address}
        onChange={handleInputChange}
        className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
      />
    </div>

    {/* Postcode */}
    <label htmlFor="postcode" className="block text-sm font-medium text-gray-900">
      Postcode
    </label>
    <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
      <input
        id="postcode"
        name="postcode"
        type="text"
        value={formData.postcode}
        onChange={handleInputChange}
        className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
      />
    </div>

    {/* Calibrate Driving Time Button */}
    <div className="sm:col-span-3 sm:py-6">
      <button
        onClick={handleDrivingTime}
        type="button"
        data-time-saved="20"
        data-component = "calibrate-driving-time"
        data-feature = "driving-time-calibration"
        className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        Calibrate Driving Time
      </button>
    </div>
    </div>
)}


    {/* Conditional Driving Time and Distance Display */}
    {drivingTime && (
      <>
       <div className="mt-8 flow-root w-full ml-10">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Single Journey Driving Time (Excluding Traffic)
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Single Journey Distance  
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Return Journey Driving Time (Excluding Traffic)
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Return Journey Distance
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Total Meeting Time (Including Meeting Length & Traffic)
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Estimated Leaving Time (Including 15 min buffer)
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Estimated Return Time (Including Meeting Length & Traffic)
                    </th>


                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                 
                    <tr>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {drivingTime[0].drivingTime}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{drivingTime[0].distance} Miles</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{drivingTime[0].returnDrivingTime} </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{drivingTime[0].returnDistance}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{drivingTime[0].totalMeetingTime}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{drivingTime[0].estimatedLeavingTime}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{drivingTime[0].estimatedReturnTime}</td>


                    </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    
      </>
    )}



                            

                           



                            {/* Attendees (Clickable list of staff) */}
                            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                <label htmlFor="staff" className="block text-sm/6 font-medium text-gray-900">
                                    Select Attendees
                                </label>
                                <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
                                    <div className="space-y-2">
                                        {staff.map((staffMember) => (
                                            <button
                                                type="button"
                                                key={staffMember.id}
                                                onClick={() => toggleAttendee(staffMember.Name)}
                                                className={`block w-full text-left p-2 rounded-md text-xs sm:text-sm ${
                                                    formData.attendees.includes(staffMember.Name)
                                                        ? 'bg-indigo-600 text-white'
                                                        : 'bg-gray-200 text-gray-900'
                                                }`}
                                            >
                                                {staffMember.Name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Display selected attendees */}
                            <div className="mt-4">
                                <h3 className="text-sm font-medium text-gray-900">Selected Attendees:</h3>
                                <ul className="mt-2 space-y-1">
                                    {formData.attendees.map((attendee, index) => (
                                        <li key={index} className="text-sm text-gray-700">
                                            {attendee}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            {formData.location !== 'Client Visits' && (
                            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                <label htmlFor="type" className="block text-sm/6 font-medium text-gray-900">
                                    Meeting Type
                                </label>
                                <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
                                    <select
                                        id="type"
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                        className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"

                                    >
                                        <option value="" disabled>Select Meeting Type</option>
                                        <option value ="Project Meeting">Project Meeting</option>
                                        <option value="Training">Training</option>
                                        <option value="Team Meeting">Team Meeting</option>
                                        <option value="Appraisal">Appraisal</option>
                                        <option value="Disciplinary">Disciplinary</option>
                                        <option value="Sales">Sales</option>
                                    </select>
                                </div>
                            </div>
                            )}
                            {formData.type === 'Project Meeting' && (
    <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
    <label htmlFor="project" className="block text-sm/6 font-medium text-gray-900">
        Select Project
    </label>
    <select
        id="project"
        name="project"
        value={formData.project}
        onChange={handleInputChange}
        className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
    >
        <option value="" disabled>Select Project</option>
        {projects.map((project) => (
            <option key={project.id} value={project.id}>
                {project.Name}
            </option>
        ))}
    </select>
</div>
)}
                            {formData.type === 'Appraisal' && (
    <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
    <label htmlFor="appraisal" className="block text-sm/6 font-medium text-gray-900">
        Select Appraisal
    </label>
    <select
        id="appraisal"
        name="appraisal"
        value={formData.appraisal}
        onChange={handleInputChange}
        className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
    >
        <option value="" disabled>Select Appraisal</option>
        {appraisal.map((a) => (
            <option key={a.id} value={a.id}>
                {format(new Date(a.ReviewPeriodFrom), 'dd/MM/yyyy')} to {format(new Date(a.ReviewPeriodTo), 'dd/MM/yyyy')}
            </option>
        ))}
    </select>
</div>
)}
{formData.type === 'Disciplinary' && (
    <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
    <label htmlFor="appraisal" className="block text-sm/6 font-medium text-gray-900">
        Select Disciplinary
    </label>
    <select
        id="appraisal"
        name="appraisal"
        value={formData.appraisal}
        onChange={handleInputChange}
        className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
    >
        <option value="" disabled>Select Appraisal</option>
        {appraisal.map((a) => (
            <option key={a.id} value={a.id}>
{a.StaffName} (Perpetrator) – {format(new Date(a.DateOfIncident), 'dd/MM/yyyy')} – Reported by {a.Author} - Witness {a.Witnesses}
</option>
        ))} 
    </select>
    <label htmlFor = "status" className="block text-sm/6 font-medium text-gray-900">
        Select Disciplinary
    </label>
    <select 
    
        id="status"
        name="status"
        value={formData.status}
        onChange={handleInputChange}
        className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
    >
        <option value="" disabled selected>Select Status</option>
<option value="Meeting With Accuser">Meeting with Accuser</option>
<option value="Meeting with Witnesses">Meeting with Witnesses</option>
<option value="Meeting With Perpertrator">Meeting with Perpetrator</option>
<option value="Final Meeting">Final Meeting</option>


        </select>



</div>
)}

                            {/* Training Document */}
                            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
    <label htmlFor="trainingDocument" className="block text-sm/6 font-medium text-gray-900">
        Add A Training Document (Optional)
    </label>
    <select
        id="trainingDocument"
        name="trainingDocument"
        value={formData.trainingDocument}
        onChange={handleInputChange}
        className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
    >
        <option value="" disabled>Select Training Document</option>
        <option value="Task">Task Graphics</option>
        <option value="Brainstorm">Brainstorm</option>
        {trainingDocuments.map((doc) => (
            doc.TrainingDocName && (
                <option key={doc.TrainingDocName} value={doc.TrainingDocName}>
                    {doc.TrainingDocName}
                </option>
            )
        ))}
    </select>
</div>

                            {/* Notes */}
                            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                <label htmlFor="notes" className={labelClass}>
                                    Notes
                                </label>
                                <div className="mt-2 col-span-1 sm:col-span-2 sm:mt-0">
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        rows={10}
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        className={controlClass}
                                    />
                                </div>
                            </div>

                            <div className="mt-6 flex items-center justify-end gap-x-6">
                                <button
                                    type="submit"
                                    data-component="meeting-form"
                                    data-time-saved="5"
                                    data-feature="create_meeting_post"
                                    className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-950/35 hover:from-indigo-800/95 hover:to-indigo-600/95 focus:outline-none focus:ring-2 focus:ring-indigo-300/70"
                                >
                                    Save Meeting
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            <div className="w-full lg:col-span-5 rounded-2xl bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border border-indigo-200/60 shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-4 sm:p-6">
                <h3 className="text-base font-semibold text-indigo-900">Calendar Preview</h3>
                <p className="mt-1 text-sm text-indigo-700">See your selected date and schedule context while creating the meeting.</p>
                <div className="mt-4 rounded-xl border border-indigo-200/70 bg-white/70 p-2">
                    <MeetingCalendar selectedDateProp={date} />
                </div>
            </div>
            </div>
            </div>

            
        </>
    );
}
